/** A block of the small Markdown subset the manual and the legal texts use. */
export type Block =
  | { kind: 'heading'; level: 1 | 2 | 3; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'list'; ordered: boolean; items: string[] }

/** A heading's address within its page: its words in lower case, joined by hyphens. */
export function anchor(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-|-$/g, '')
}

/**
 * Headings (#, ##, ###), paragraphs and lists (- or 1.); other lines are paragraph text, joined by a
 * space, or by a line break where the writer's line breaks count (`breaks`).
 */
export function parseBlocks(markdown: string, { breaks = false }: { breaks?: boolean } = {}): Block[] {
  const blocks: Block[] = []
  let paragraph: string[] = []
  let list: Extract<Block, { kind: 'list' }> | undefined
  const endParagraph = () => {
    if (paragraph.length) blocks.push({ kind: 'paragraph', text: paragraph.join(breaks ? '\n' : ' ') })
    paragraph = []
  }
  const endList = () => {
    if (list) blocks.push(list)
    list = undefined
  }
  for (const line of markdown.replace(/\r\n/g, '\n').split('\n')) {
    const heading = /^(#{1,3})\s+(.+)$/.exec(line)
    const item = /^\s*(?:[-*]|(\d+)\.)\s+(.+)$/.exec(line)
    if (heading) {
      endParagraph()
      endList()
      blocks.push({ kind: 'heading', level: heading[1]!.length as 1 | 2 | 3, text: heading[2]!.trim() })
    } else if (item) {
      endParagraph()
      const ordered = item[1] !== undefined
      if (list && list.ordered !== ordered) endList()
      list ??= { kind: 'list', ordered, items: [] }
      list.items.push(item[2]!.trim())
    } else if (!line.trim()) {
      endParagraph()
      endList()
    } else {
      endList()
      paragraph.push(line.trim())
    }
  }
  endParagraph()
  endList()
  return blocks
}
